import React, { forwardRef } from 'react';
import { clsx } from '@mentimeter/ragnar-tailwind-config';
import { Clickable } from '../../../clickable/tw';
import { IconContainer } from '../../icon-container';
import { iconButtonVariants } from './IconButton.variants';
import { type IconButtonProps } from './IconButton.types';

export const IconButton = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  IconButtonProps
>(
  (
    { children, borderRadius, size, variant, disabled, className, ...rest },
    forwardedRef,
  ) => {
    const status = rest?.status;
    const stateVariant = status ? (status as 'success' | 'error') : undefined;
    const hasBlockingStateVariant =
      stateVariant === 'success' || stateVariant === 'error';
    const hasStatus = Boolean(status);

    const defaultClasses = clsx([
      'group',
      'items-center',
      'justify-center',
      'focus-visible:z-1',
      'cursor-pointer',
      'transition',
      'ease-in-out',
      'duration-[0.1s]',
      'enabled:active:scale-90',
      'enabled:active:opacity-100',
      'enabled:hover:opacity-100',
      'disabled:cursor-not-allowed',
      'disabled:opacity-100',
      'disabled:hover:opacity-100',
      '[&:disabled]:data-[has-status="false"]:bg-disabled-strong',
      '[&:disabled]:data-[has-status="false"]:hover:bg-disabled-strong',
      '[&:disabled]:data-[has-status="false"]:focus-visible:bg-disabled-strong',
      '[&:disabled]:data-[has-status="false"]:text-on-disabled-strong',
    ]);

    const iconButtonClasses = iconButtonVariants({
      variant: hasBlockingStateVariant ? null : variant,
      borderRadius,
      size,
      status: !disabled ? stateVariant : null,
    });

    return (
      <Clickable
        ref={forwardedRef}
        type="button"
        disabled={hasStatus || disabled}
        data-has-status={!disabled && hasStatus}
        className={clsx(defaultClasses, iconButtonClasses, className)}
        {...rest}
      >
        <IconContainer status={!disabled ? status : null} size={size}>
          {children}
        </IconContainer>
      </Clickable>
    );
  },
);
